/**
 * OptionsPage — Configuración del portal de cliente.
 *
 * Tabs:
 *  - Datos personales (PUT /users/me)
 *  - Domicilio (PUT /users/me — address + stateId)
 *  - Cuenta bancaria (placeholder — backend listo, falta UI completa)
 *  - Seguridad (toggle modo oscuro)
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import { useMe, useUpdateMe } from '@modules/users/presentation/hooks/useUsers';
import { useTheme } from '@providers/ThemeProvider';
import { getErrorMessage } from '@shared/infrastructure/http/api.error';
import { toast } from 'sonner';
import { CheckCircle2, Landmark, Moon, Pencil } from 'lucide-react';

interface PersonalForm {
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: string;
}

interface AddressForm {
  address: string;
  stateId: string;
}

interface ContactForm {
  phone: string;
}

export function OptionsPage() {
  const { data: profile, isLoading } = useMe();
  const updateMe = useUpdateMe();
  const { theme, setTheme } = useTheme();
  const [editEmail, setEditEmail] = useState(false);

  const personalForm = useForm<PersonalForm>({
    defaultValues: { firstName: '', lastName: '', birthDate: '', gender: '' },
  });
  const contactForm = useForm<ContactForm>({ defaultValues: { phone: '' } });
  const addressForm = useForm<AddressForm>({ defaultValues: { address: '', stateId: '' } });

  // Resync forms cuando carga profile
  useEffect(() => {
    if (!profile) return;
    personalForm.reset({
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      birthDate: profile.birthDate ?? '',
      gender: profile.gender ?? '',
    });
    contactForm.reset({ phone: profile.phone ?? '' });
    addressForm.reset({ address: profile.address ?? '', stateId: profile.stateId ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const onSavePersonal = async (values: PersonalForm) => {
    try {
      await updateMe.mutateAsync(values);
      toast.success('Datos personales actualizados');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const onSaveContact = async (values: ContactForm) => {
    try {
      await updateMe.mutateAsync(values);
      toast.success('Datos de contacto actualizados');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const onSaveAddress = async (values: AddressForm) => {
    try {
      await updateMe.mutateAsync(values);
      toast.success('Domicilio actualizado');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  if (isLoading || !profile) {
    return (
      <div className="space-y-6">
        <PageHeader title="Configuración" subtitle="Administra tu información personal y preferencias" />
        <Skeleton className="h-12 w-full rounded-2xl" />
        <Skeleton className="h-80 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Configuración" subtitle="Administra tu información personal y preferencias" />

      <Tabs defaultValue="datos" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 gap-1 sm:w-auto sm:grid-cols-4">
          <TabsTrigger value="datos" className="text-xs sm:text-sm">
            <span className="sm:hidden">Datos</span>
            <span className="hidden sm:inline">Datos personales</span>
          </TabsTrigger>
          <TabsTrigger value="domicilio" className="text-xs sm:text-sm">
            Domicilio
          </TabsTrigger>
          <TabsTrigger value="banco" className="text-xs sm:text-sm">
            <span className="sm:hidden">Banco</span>
            <span className="hidden sm:inline">Cuenta bancaria</span>
          </TabsTrigger>
          <TabsTrigger value="seguridad" className="text-xs sm:text-sm">
            Seguridad
          </TabsTrigger>
        </TabsList>

        {/* ───── Datos personales ───── */}
        <TabsContent value="datos" className="space-y-4">
          {/* Card Datos Personales */}
          <form
            onSubmit={personalForm.handleSubmit(onSavePersonal)}
            className="rounded-2xl border border-border bg-card p-6 shadow-xs"
          >
            <div className="mb-5 flex items-center gap-3 border-l-4 border-sidebar-primary pl-3">
              <div>
                <h2 className="text-lg font-bold tracking-tight">Datos personales</h2>
                <p className="text-xs text-muted-foreground">Datos que usamos en tus documentos y comunicaciones.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="dni">RUT</Label>
                <Input id="dni" value={profile.dni ?? ''} readOnly disabled />
                <p className="text-xs text-muted-foreground">El RUT no se puede modificar.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="firstName">Nombre</Label>
                <Input id="firstName" {...personalForm.register('firstName')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">Apellido</Label>
                <Input id="lastName" {...personalForm.register('lastName')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="birthDate">Fecha de nacimiento</Label>
                <Input
                  id="birthDate"
                  type="date"
                  {...personalForm.register('birthDate')}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="gender">Género</Label>
                <Input id="gender" placeholder="Femenino, Masculino, Otro" {...personalForm.register('gender')} />
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <Button
                type="submit"
                className="rounded-full"
                disabled={updateMe.isPending || !personalForm.formState.isDirty}
              >
                {updateMe.isPending ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </form>

          {/* Card Datos de Contacto */}
          <form
            onSubmit={contactForm.handleSubmit(onSaveContact)}
            className="rounded-2xl border border-border bg-card p-6 shadow-xs"
          >
            <div className="mb-5 flex items-center gap-3 border-l-4 border-emerald-500 pl-3">
              <div>
                <h2 className="text-lg font-bold tracking-tight">Datos de contacto</h2>
                <p className="text-xs text-muted-foreground">Cómo nos comunicamos contigo.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Input
                    id="email"
                    value={profile.email}
                    disabled={!editEmail}
                    readOnly={!editEmail}
                  />
                  {profile.emailVerified && !editEmail && (
                    <CheckCircle2
                      className="absolute right-9 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500"
                      aria-label="Email verificado"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setEditEmail((e) => !e)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label="Editar email"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
                {editEmail && (
                  <p className="text-xs text-muted-foreground">
                    Cambiar tu email te enviará un correo de verificación (próximamente).
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Teléfono</Label>
                <div className="flex items-center gap-2">
                  <span className="rounded-lg border border-border bg-muted px-2.5 py-1.5 text-sm text-muted-foreground">
                    +56 9
                  </span>
                  <Input
                    id="phone"
                    {...contactForm.register('phone')}
                    placeholder="1234 5678"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <Button
                type="submit"
                className="rounded-full"
                disabled={updateMe.isPending || !contactForm.formState.isDirty}
              >
                {updateMe.isPending ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* ───── Domicilio ───── */}
        <TabsContent value="domicilio" className="space-y-4">
          <form
            onSubmit={addressForm.handleSubmit(onSaveAddress)}
            className="rounded-2xl border border-border bg-card p-6 shadow-xs"
          >
            <div className="mb-5">
              <h2 className="text-lg font-bold tracking-tight">Domicilio</h2>
              <p className="text-xs text-muted-foreground">
                Dirección de envío para documentos y mercadería.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  {...addressForm.register('address')}
                  placeholder="Calle, número, depto."
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="stateId">Comuna (ID temporal)</Label>
                <Input
                  id="stateId"
                  {...addressForm.register('stateId')}
                  placeholder="ID de comuna (selector visual próximamente)"
                />
                <p className="text-xs text-muted-foreground">
                  Selector encadenado Región → Comuna en próxima iteración.
                </p>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <Button
                type="submit"
                className="rounded-full"
                disabled={updateMe.isPending || !addressForm.formState.isDirty}
              >
                {updateMe.isPending ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* ───── Cuenta bancaria ───── */}
        <TabsContent value="banco" className="space-y-4">
          <EmptyState
            icon={Landmark}
            title="Próximamente"
            description="La gestión de tu cuenta bancaria para recibir reembolsos estará disponible muy pronto. El backend ya soporta estos endpoints; estamos terminando el flujo de UI."
          />
        </TabsContent>

        {/* ───── Seguridad ───── */}
        <TabsContent value="seguridad" className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-xs">
            <div className="mb-5">
              <h2 className="text-lg font-bold tracking-tight">Apariencia</h2>
              <p className="text-xs text-muted-foreground">Personaliza cómo se ve el portal.</p>
            </div>

            <div className="flex items-center justify-between rounded-xl border border-border bg-muted/40 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                  <Moon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground">Modo oscuro</p>
                  <p className="text-xs text-muted-foreground">
                    Reduce el brillo para usar mishiwoof de noche.
                  </p>
                </div>
              </div>
              <Switch
                checked={isDark}
                onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              />
            </div>
          </div>

          <Separator className="my-2" />

          <div className="rounded-2xl border border-border bg-card p-6 shadow-xs">
            <div className="mb-5 flex items-center gap-3 border-l-4 border-destructive pl-3">
              <div>
                <h2 className="text-lg font-bold tracking-tight">Seguridad de la cuenta</h2>
                <p className="text-xs text-muted-foreground">
                  Próximamente: cambiar contraseña y activar 2FA.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-full" disabled>
                Cambiar contraseña
              </Button>
              <Button variant="outline" className="rounded-full" disabled>
                Activar 2FA
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
